import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Edit, Trash2, Building, Settings } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { apiClient } from '../../utils/apiHelper';

const FeeSetting = () => {
  const [fees, setFees] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHostelFeeModalOpen, setIsHostelFeeModalOpen] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [editingHostel, setEditingHostel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('hostel-fees');
  
  const [formData, setFormData] = useState({ 
    fee_name: '', 
    fee_type: 'Monthly', 
    amount: 0, 
    description: '' 
  });
  
  const [hostelFeeData, setHostelFeeData] = useState({
    monthly_fee: 0,
    security_deposit: 0,
    admission_fee: 0,
    maintenance_fee: 0
  });

  useEffect(() => { 
    fetchFees(); 
    fetchHostels();
  }, []);

  const fetchFees = async () => {
    try { 
      const response = await apiClient.get('/api/hostel/fee-settings'); 
      setFees(Array.isArray(response.data) ? response.data : []); 
    } catch (error) { 
      console.error('Error:', error); 
      setFees([]);
    }
  };

  const fetchHostels = async () => {
    try {
      const response = await apiClient.get('/api/hostel/hostels');
      setHostels(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
      setHostels([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingFee) {
        await apiClient.put(`/api/hostel/fee-settings/${editingFee.id}`, formData);
      } else {
        await apiClient.post('/api/hostel/fee-settings', formData);
      }
      alert('Fee setting saved!'); 
      fetchFees(); 
      handleCloseModal();
    } catch (error) { 
      alert('Failed'); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleHostelFeeSubmit = async (e) => {
    e.preventDefault();
    if (!editingHostel) return;
    
    setLoading(true);
    try {
      // Update hostel with fee settings
      await apiClient.put(`/api/hostel/hostels/${editingHostel.id}`, {
        ...editingHostel,
        monthly_fee: hostelFeeData.monthly_fee,
        security_deposit: hostelFeeData.security_deposit,
        admission_fee: hostelFeeData.admission_fee,
        maintenance_fee: hostelFeeData.maintenance_fee
      });
      alert('Hostel fee updated!');
      fetchHostels();
      setIsHostelFeeModalOpen(false);
      setEditingHostel(null);
    } catch (error) {
      alert('Failed to update');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete?')) { 
      await apiClient.delete(`/api/hostel/fee-settings/${id}`); 
      fetchFees(); 
    }
  };

  const handleEdit = (fee) => {
    setEditingFee(fee);
    setFormData({ fee_name: fee.fee_name, fee_type: fee.fee_type, amount: fee.amount, description: fee.description || '' });
    setIsModalOpen(true);
  };

  const handleHostelFeeEdit = (hostel) => {
    setEditingHostel(hostel);
    setHostelFeeData({
      monthly_fee: hostel.monthly_fee || 0,
      security_deposit: hostel.security_deposit || 0,
      admission_fee: hostel.admission_fee || 0,
      maintenance_fee: hostel.maintenance_fee || 0
    });
    setIsHostelFeeModalOpen(true);
  };

  const handleCloseModal = () => { 
    setIsModalOpen(false); 
    setEditingFee(null); 
    setFormData({ fee_name: '', fee_type: 'Monthly', amount: 0, description: '' }); 
  };

  return (
    <div className="p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Hostel Fee Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="hostel-fees">Hostel Wise Fees</TabsTrigger>
              <TabsTrigger value="fee-types">Fee Types</TabsTrigger>
            </TabsList>

            {/* Hostel Wise Fees Tab */}
            <TabsContent value="hostel-fees">
              <div className="mb-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <strong>Note:</strong> Set monthly fee for each hostel. This fee will be auto-filled when adding students to that hostel.
              </div>
              
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3">Hostel Name</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-right p-3">Monthly Fee</th>
                    <th className="text-right p-3">Security Deposit</th>
                    <th className="text-right p-3">Admission Fee</th>
                    <th className="text-right p-3">Maintenance</th>
                    <th className="text-center p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {hostels.length === 0 ? (
                    <tr><td colSpan="7" className="text-center p-4 text-gray-500">No hostels found. Add hostels first.</td></tr>
                  ) : (
                    hostels.map((hostel) => (
                      <tr key={hostel.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{hostel.hostel_name}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                            {hostel.hostel_type || 'General'}
                          </span>
                        </td>
                        <td className="p-3 text-right font-medium text-green-600">
                          ₹{hostel.monthly_fee || 0}
                        </td>
                        <td className="p-3 text-right">₹{hostel.security_deposit || 0}</td>
                        <td className="p-3 text-right">₹{hostel.admission_fee || 0}</td>
                        <td className="p-3 text-right">₹{hostel.maintenance_fee || 0}</td>
                        <td className="p-3 text-center">
                          <Button size="sm" variant="outline" onClick={() => handleHostelFeeEdit(hostel)}>
                            <Edit className="w-3 h-3 mr-1" />Set Fees
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TabsContent>

            {/* Fee Types Tab */}
            <TabsContent value="fee-types">
              <div className="flex justify-end mb-4">
                <Button onClick={() => setIsModalOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />Add Fee Type
                </Button>
              </div>
              
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3">Fee Name</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-right p-3">Amount</th>
                    <th className="text-left p-3">Description</th>
                    <th className="text-center p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fees.length === 0 ? (
                    <tr><td colSpan="5" className="text-center p-4 text-gray-500">No fee types found.</td></tr>
                  ) : (
                    fees.map((fee) => (
                      <tr key={fee.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{fee.fee_name}</td>
                        <td className="p-3">
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">{fee.fee_type}</span>
                        </td>
                        <td className="p-3 text-right">₹{fee.amount}</td>
                        <td className="p-3">{fee.description || 'N/A'}</td>
                        <td className="p-3 text-center">
                          <div className="flex gap-2 justify-center">
                            <Button size="sm" variant="outline" onClick={() => handleEdit(fee)}>
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDelete(fee.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Hostel Fee Edit Modal */}
      <Dialog open={isHostelFeeModalOpen} onOpenChange={setIsHostelFeeModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Fees - {editingHostel?.hostel_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleHostelFeeSubmit} className="space-y-4">
            <div>
              <Label>Monthly Fee (₹) *</Label>
              <Input 
                type="number" 
                step="0.01" 
                required 
                value={hostelFeeData.monthly_fee} 
                onChange={(e) => setHostelFeeData({ ...hostelFeeData, monthly_fee: parseFloat(e.target.value) || 0 })} 
              />
              <p className="text-xs text-gray-500 mt-1">This fee will be charged every month</p>
            </div>
            <div>
              <Label>Security Deposit (₹)</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={hostelFeeData.security_deposit} 
                onChange={(e) => setHostelFeeData({ ...hostelFeeData, security_deposit: parseFloat(e.target.value) || 0 })} 
              />
              <p className="text-xs text-gray-500 mt-1">One-time refundable deposit at admission</p>
            </div>
            <div>
              <Label>Admission Fee (₹)</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={hostelFeeData.admission_fee} 
                onChange={(e) => setHostelFeeData({ ...hostelFeeData, admission_fee: parseFloat(e.target.value) || 0 })} 
              />
              <p className="text-xs text-gray-500 mt-1">One-time non-refundable fee at admission</p>
            </div>
            <div>
              <Label>Maintenance Fee (₹/month)</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={hostelFeeData.maintenance_fee} 
                onChange={(e) => setHostelFeeData({ ...hostelFeeData, maintenance_fee: parseFloat(e.target.value) || 0 })} 
              />
            </div>
            
            <div className="bg-gray-100 p-3 rounded-lg">
              <p className="font-medium">Total Monthly: ₹{hostelFeeData.monthly_fee + hostelFeeData.maintenance_fee}</p>
              <p className="text-sm text-gray-600">One-time at Admission: ₹{hostelFeeData.security_deposit + hostelFeeData.admission_fee}</p>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsHostelFeeModalOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save Fees'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Fee Type Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingFee ? 'Edit' : 'Add'} Fee Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Fee Name *</Label>
              <Input required value={formData.fee_name} onChange={(e) => setFormData({ ...formData, fee_name: e.target.value })} placeholder="e.g., Monthly Rent" />
            </div>
            <div>
              <Label>Fee Type *</Label>
              <Select value={formData.fee_type} onValueChange={(value) => setFormData({ ...formData, fee_type: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                  <SelectItem value="Security Deposit">Security Deposit</SelectItem>
                  <SelectItem value="Admission">Admission</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (₹) *</Label>
              <Input type="number" step="0.01" required value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })} />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
              <Button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeeSetting;
